import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faHeadphones } from '@fortawesome/pro-light-svg-icons/faHeadphones';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faReply } from '@fortawesome/pro-light-svg-icons/faReply';
import { faStar } from '@fortawesome/pro-light-svg-icons/faStar';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { TypedMemo } from '../../../../common/types';
import type { TimelineStep } from './pitchTimeline';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the details panel */
import styles from './Details.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ICON: Record<TimelineStep['kind'], IconDefinition> = {
  sent: faPaperPlane,
  followUp: faPaperPlane,
  opened: faEye,
  notOpened: faEye,
  listened: faHeadphones,
  clicked: faArrowUpRightFromSquare,
  replied: faReply,
  placed: faStar,
  followUpNext: faClock,
  found: faStar,
  ready: faStar,
};

interface PitchTimelineListProps {
  steps: TimelineStep[];
  /** The action of the waiting follow-up ("Cancel the follow-up"). */
  followUpAction: React.ReactNode;
}

/** What happened to the pitch, oldest first (design outreach-v2 t2, t3). */
const PitchTimelineListComponent: React.FC<PitchTimelineListProps> = ({ steps, followUpAction }) => {
  const intl = useIntl();
  return (
    <ol className={styles.timeline} data-testid="pitch-timeline">
      {steps.map((step) => (
        <li key={step.key} className={styles.step} data-kind={step.kind}>
          <span className={clsx(styles.dot, styles[`dot-${step.dot}`])} aria-hidden>
            <FontAwesomeIcon icon={ICON[step.kind]} />
          </span>
          <div>
            <div className={clsx(styles.stepTitle, step.dot === 'todo' && styles.stepTitleTodo)}>{step.title}</div>
            {step.detail !== null && <div className={styles.stepDetail}>{step.detail}</div>}
            {step.kind === 'followUpNext' && followUpAction}
          </div>
          <span className={styles.stepAt}>
            {step.at === null
              ? ''
              : intl.formatDate(new Date(step.at), {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
          </span>
        </li>
      ))}
    </ol>
  );
};

export const PitchTimelineList = TypedMemo(PitchTimelineListComponent);
